import React, { useMemo, useCallback } from 'react';
import { Select } from '@iq/react-components';
import '../widgets/SelectWidget.scss';
import './ComponentPickerField.scss';

const renderEmptyMessage = (messageText) => <div>{messageText}</div>;

const ComponentPickerField = (properties) => {
  const {
    disabled,
    readonly,
    required = true,
    formData: value,
    schema: {
      handleCreatable,
      components = [],
      default: defaultValue,
      isMulti,
      isClearable,
      noOptionsMessage,
      placeholder: schemaPlaceholder,
      label,
      valueKey = 'id',
      styles = {},
      modalState,
      editable,
    },
    onChange,
    ...otherProperties
  } = properties;

  const findComponent = useCallback(
    (componentIdentifier) => {
      if (componentIdentifier == null) return;
      return components.find(
        (component) => (component[valueKey] || component.id) === componentIdentifier
      );
    },
    [components, valueKey]
  );

  const buildComponentSelectOption = useCallback(
    (component) => ({
      value: component[valueKey] || component.id,
      label: `${component.name} (${component.itemDesignation})`,
    }),
    [valueKey]
  );

  const options = useMemo(
    () => components.map(buildComponentSelectOption),
    [components, buildComponentSelectOption]
  );

  const selectedOption = useMemo(() => {
    if (isMulti) {
      return (value || [])
        .map((componentIdentifier) => {
          const matchingComponent = findComponent(componentIdentifier);
          if (matchingComponent) {
            return buildComponentSelectOption(matchingComponent);
          }
          if (handleCreatable) {
            return {
              value: componentIdentifier,
              label: componentIdentifier,
            };
          }
          return null;
        })
        .filter(Boolean);
    }

    const componentIdentifier = value ?? defaultValue;
    if (componentIdentifier || componentIdentifier === 0) {
      const matchingComponent = findComponent(componentIdentifier);
      if (matchingComponent) {
        return buildComponentSelectOption(matchingComponent);
      }
      if (handleCreatable) {
        return {
          value: componentIdentifier,
          label: componentIdentifier,
        };
      }
    }
    return null;
  }, [value, defaultValue, findComponent, isMulti, handleCreatable, buildComponentSelectOption]);

  const selectedComponents = useMemo(() => {
    if (isMulti && value && value.length > 0) {
      return value.map((componentIdentifier) => findComponent(componentIdentifier)).filter(Boolean);
    }
    if (value || value === 0) {
      const matchingComponent = findComponent(value);
      return matchingComponent ? [matchingComponent] : [];
    }
    return [];
  }, [value, isMulti, findComponent]);

  const handleSelectChange = useCallback(
    (selectedOptionOrOptions) => {
      if (isMulti) {
        onChange((selectedOptionOrOptions || []).map((option) => option.value));
      } else {
        onChange(selectedOptionOrOptions?.value ?? null);
      }
    },
    [onChange, isMulti]
  );

  const overrideStyles = useMemo(
    () => ({
      container: (base) => ({ ...base, lineHeight: 'normal', ...styles.container }),
      singleValue: (base) => ({ ...base, lineHeight: 'normal', ...styles.singleValue }),
      control: (base) => ({ ...base, lineHeight: 'normal', ...styles.control }),
    }),
    [styles]
  );

  const isViewMode = editable === false || modalState === 'view' || readonly || disabled;

  const LabelComponent = ({ showRequired = true }) =>
    label && (
      <label className="control-label">
        {label}
        {required && showRequired && <span className="required">*</span>}
      </label>
    );

  const ViewModeContainer = ({ children }) => (
    <div className="component-picker-field">
      <LabelComponent showRequired={false} />
      {children}
    </div>
  );

  if (isViewMode) {
    return (
      <ViewModeContainer>
        {selectedComponents.length > 0 ? (
          <div className="selected-components-display">
            {selectedComponents.map((component) => (
              <div
                key={component.id}
                className="selected-component-item"
              >
                <span className="component-name">{component.name}</span>
                <span className="component-designation">({component.itemDesignation})</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="selected-components-display">No components selected</div>
        )}
      </ViewModeContainer>
    );
  }

  return (
    <div className="component-picker-field">
      <LabelComponent />
      <Select
        {...otherProperties}
        className="schema-select-widget"
        isMulti={isMulti}
        isClearable={isClearable}
        closeMenuOnSelect={!isMulti}
        disabled={disabled}
        readOnly={readonly}
        required={required}
        placeholder={schemaPlaceholder}
        menuPlacement="auto"
        value={selectedOption}
        onChange={handleSelectChange}
        onCreateOption={handleCreatable}
        options={options}
        noOptionsMessage={() => renderEmptyMessage(noOptionsMessage || 'No options')}
        styles={overrideStyles}
      />
    </div>
  );
};

export default ComponentPickerField;
